import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '../components/ui/dialog';
import {
  Play,
  Pause,
  Volume2,
  VolumeX,
  Maximize,
  Minimize,
  SkipBack,
  SkipForward,
  CheckCircle,
  Circle,
  Lock,
  Loader2,
  ArrowLeft,
  Clock,
  BookOpen,
  FileQuestion,
  Award,
  XCircle,
  RotateCcw,
  CreditCard,
  FileText,
  Send,
  Camera,
  CameraOff,
  Shield,
  ChevronUp,
  List
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useFaceMonitor } from '../components/FaceMonitor';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Lighten resource usage on phones/tablets (reduce buffering/freezing)
const IS_MOBILE_DEVICE = typeof navigator !== 'undefined' && (
  /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent) ||
  (typeof window !== 'undefined' && window.innerWidth < 768)
);

export default function CourseWatch() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { language, t } = useLanguage();
  const { user, isAuthenticated, loading: authLoading, getAuthHeaders } = useAuth();
  const locQuiz = (val) => {
    if (val == null) return '';
    if (typeof val === 'string') return val;
    const lng = user?.language || language || 'ja';
    return val[lng] || val.ja || Object.values(val)[0] || '';
  };
  
  const [course, setCourse] = useState(null);
  const [videos, setVideos] = useState([]);
  const [currentVideo, setCurrentVideo] = useState(null);
  const [videoToken, setVideoToken] = useState('');
  const [progress, setProgress] = useState({});
  const [loading, setLoading] = useState(true);
  const [videoLoading, setVideoLoading] = useState(false);
  const [videoBuffering, setVideoBuffering] = useState(false);
  
  // Quiz states
  const [showQuiz, setShowQuiz] = useState(false);
  const [quiz, setQuiz] = useState(null);
  const [quizLoading, setQuizLoading] = useState(false);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizResult, setQuizResult] = useState(null);
  const [submittingQuiz, setSubmittingQuiz] = useState(false);
  const [quizPassed, setQuizPassed] = useState({});  // Track passed quizzes by videoId

  // Certificate request states
  const [showCertDialog, setShowCertDialog] = useState(false);
  const [certRequestType, setCertRequestType] = useState(null);
  const [selectedCertTypes, setSelectedCertTypes] = useState([]); // For multiple selection (card, digital)
  const [submittingCertRequest, setSubmittingCertRequest] = useState(false);
  
  // Video player states
  const videoRef = useRef(null);
  const progressIntervalRef = useRef(null);

  // Refs for auto-scrolling the current video to the top of the video list
  const desktopListRef = useRef(null);
  const mobileListRef = useRef(null);
  const desktopCurrentRef = useRef(null);
  const mobileCurrentRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [showControls, setShowControls] = useState(true);
  const [showMobileList, setShowMobileList] = useState(false);
  const controlsTimeoutRef = useRef(null);
  const monitorLogBufferRef = useRef([]);
  const autoPausedRef = useRef(false);

  // Furthest naturally-watched position (seconds) for the current video - used to gate fast-forward
  const maxWatchedRef = useRef(0);

  // Warning alarm (Web Audio API) - beeps repeatedly (max 5s) while face is not detected
  const alarmRef = useRef({ ctx: null, interval: null, timeout: null });

  const initAlarm = useCallback(() => {
    const a = alarmRef.current;
    try {
      if (!a.ctx) a.ctx = new (window.AudioContext || window.webkitAudioContext)();
      if (a.ctx.state === 'suspended') a.ctx.resume();
    } catch (e) { /* audio not supported */ }
  }, []);

  const stopAlarm = useCallback(() => {
    const a = alarmRef.current;
    if (a.interval) {
      clearInterval(a.interval);
      a.interval = null;
    }
    if (a.timeout) {
      clearTimeout(a.timeout);
      a.timeout = null;
    }
  }, []);

  const startAlarm = useCallback(() => {
    const a = alarmRef.current;
    if (a.interval) return; // already sounding
    try {
      if (!a.ctx) a.ctx = new (window.AudioContext || window.webkitAudioContext)();
      if (a.ctx.state === 'suspended') a.ctx.resume();
      const beep = () => {
        const ctx = a.ctx;
        if (!ctx) return;
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'square';
        osc.frequency.value = 880;
        gain.gain.setValueAtTime(0.0001, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.35, ctx.currentTime + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.25);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start();
        osc.stop(ctx.currentTime + 0.26);
      };
      beep();
      a.interval = setInterval(beep, 700);
      // Auto-stop the alarm after 5 seconds (video stays paused until face returns)
      a.timeout = setTimeout(() => { stopAlarm(); }, 5000);
    } catch (e) { /* audio not supported */ }
  }, [stopAlarm]);

  // Face monitoring
  const handleFaceLost = useCallback(() => {
    if (videoRef.current && !videoRef.current.paused) {
      videoRef.current.pause();
      setIsPlaying(false);
      autoPausedRef.current = true;
    }
    startAlarm();
    // Log face lost event
    monitorLogBufferRef.current.push({
      courseId: courseId,
      videoId: currentVideo?.id || '',
      eventType: 'face_lost',
      timestamp: new Date().toISOString(),
      details: { videoTime: videoRef.current?.currentTime || 0 }
    });
  }, [courseId, currentVideo?.id, startAlarm]);

  const handleFaceFound = useCallback(() => {
    stopAlarm();
    // Auto-resume only if the video was auto-paused due to face lost
    if (autoPausedRef.current && videoRef.current) {
      autoPausedRef.current = false;
      videoRef.current.play().then(() => setIsPlaying(true)).catch(() => {});
    }
    monitorLogBufferRef.current.push({
      courseId: courseId,
      videoId: currentVideo?.id || '',
      eventType: 'face_found',
      timestamp: new Date().toISOString(),
      details: { videoTime: videoRef.current?.currentTime || 0 }
    });
  }, [courseId, currentVideo?.id, stopAlarm]);

  const {
    startCamera,
    stopCamera,
    cameraActive,
    modelLoaded,
    faceDetected,
    cameraError,
    monitoring,
    CameraPreview,
    StatusBadge,
    WarningOverlay,
  } = useFaceMonitor({
    onFaceLost: handleFaceLost,
    onFaceFound: handleFaceFound,
    enabled: true,
    graceSeconds: 5
  });

  // Flush monitoring logs to backend every 30s
  useEffect(() => {
    const flush = async () => {
      if (monitorLogBufferRef.current.length === 0) return;
      const logs = [...monitorLogBufferRef.current];
      monitorLogBufferRef.current = [];
      try {
        await axios.post(`${API}/monitoring/logs/batch`, logs, { headers: getAuthHeaders() });
      } catch (e) {
        // Re-add failed logs
        monitorLogBufferRef.current.unshift(...logs);
      }
    };
    const interval = setInterval(flush, 30000);
    return () => {
      clearInterval(interval);
      flush(); // Flush on unmount
    };
  }, [getAuthHeaders]);

  // Stop alarm on unmount
  useEffect(() => {
    return () => {
      stopAlarm();
      const a = alarmRef.current;
      if (a.ctx) { try { a.ctx.close(); } catch (e) {} a.ctx = null; }
    };
  }, [stopAlarm]);

  // Redirect if not authenticated
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/register');
    }
  }, [authLoading, isAuthenticated, navigate]);

  // Fetch course and progress data
  useEffect(() => {
    const fetchCourseData = async () => {
      if (!isAuthenticated || !courseId) return;
      
      try {
        setLoading(true);
        
        // Fetch course details
        const courseRes = await axios.get(`${API}/courses/${courseId}`);
        setCourse(courseRes.data);
        
        const courseVideos = courseRes.data.videos || [];
        setVideos(courseVideos);
        
        // Fetch user's progress
        const progressRes = await axios.get(`${API}/courses/${courseId}/my-progress`, {
          headers: getAuthHeaders()
        });
        
        // Create progress map
        const progressMap = {};
        const quizPassedMap = {};
        (progressRes.data.videos || []).forEach(v => {
          progressMap[v.videoId] = v;
          if (v.quizPassed) {
            quizPassedMap[v.videoId] = true;
          }
        });
        setProgress(progressMap);
        setQuizPassed(quizPassedMap);
        
        // Set first unwatched video or first video
        if (courseVideos.length > 0) {
          const firstUnwatched = courseVideos.find(v => !progressMap[v.id]?.completed);
          setCurrentVideo(firstUnwatched || courseVideos[0]);
        }
      } catch (error) {
        console.error('Error fetching course:', error);
        toast.error('講習データの取得に失敗しました');
      } finally {
        setLoading(false);
      }
    };

    fetchCourseData();
  }, [isAuthenticated, courseId, getAuthHeaders]);

  // Video URL state
  const [videoUrl, setVideoUrl] = useState('');

  // Fetch video token when current video changes
  useEffect(() => {
    const fetchVideoToken = async () => {
      if (!currentVideo?.id) return;
      
      setCurrentTime(0);
      setDuration(0);
      setIsPlaying(false);
      try {
        setVideoLoading(true);
        const response = await axios.get(`${API}/videos/${currentVideo.id}/token`, {
          headers: getAuthHeaders()
        });
        setVideoToken(response.data.token);
        
        // Use direct Nginx URL if available, otherwise use API stream
        if (response.data.directUrl) {
          // Use the REACT_APP_BACKEND_URL base to construct the video URL
          // This ensures we're using the correct domain (jwsea.jp)
          const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
          // Remove /api suffix if present to get the base domain
          const baseUrl = backendUrl.replace(/\/api$/, '');
          setVideoUrl(`${baseUrl}${response.data.directUrl}`);
        } else {
          setVideoUrl(`${API}/videos/${currentVideo.id}/stream?token=${response.data.token}`);
        }
      } catch (error) {
        console.error('Error fetching video token:', error);
        toast.error('動画の読み込みに失敗しました');
      } finally {
        setVideoLoading(false);
      }
    };

    fetchVideoToken();
  }, [currentVideo?.id, getAuthHeaders]);

  // Auto-scroll the currently watched video to the top of the video list
  useEffect(() => {
    if (!currentVideo?.id) return;
    const scrollToTop = (container, item) => {
      if (!container || !item) return;
      const delta = item.getBoundingClientRect().top - container.getBoundingClientRect().top;
      container.scrollBy({ top: delta, behavior: 'smooth' });
    };
    const timer = setTimeout(() => {
      scrollToTop(desktopListRef.current, desktopCurrentRef.current);
      scrollToTop(mobileListRef.current, mobileCurrentRef.current);
    }, 100);
    return () => clearTimeout(timer);
  }, [currentVideo?.id]);


  // Save progress periodically
  const saveProgress = useCallback(async () => {
    if (!videoRef.current || !currentVideo?.id) return;
    
    const video = videoRef.current;
    if (video.duration <= 0) return;
    
    try {
      await axios.post(`${API}/videos/${currentVideo.id}/progress`, {
        watchedSeconds: Math.floor(video.currentTime),
        totalSeconds: Math.floor(video.duration),
        lastPosition: Math.floor(video.currentTime)
      }, {
        headers: getAuthHeaders()
      });
      
      // Update local progress
      const watchedPercent = (video.currentTime / video.duration) * 100;
      const completed = watchedPercent >= (currentVideo.requiredWatchPercent || 90);
      
      setProgress(prev => ({
        ...prev,
        [currentVideo.id]: {
          ...prev[currentVideo.id],
          watchedPercent,
          lastPosition: video.currentTime,
          completed
        }
      }));
    } catch (error) {
      console.error('Error saving progress:', error);
    }
  }, [currentVideo?.id, currentVideo?.requiredWatchPercent, getAuthHeaders]);

  // Set up progress saving interval
  useEffect(() => {
    if (isPlaying) {
      progressIntervalRef.current = setInterval(saveProgress, 10000); // Save every 10 seconds
    }
    
    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
    };
  }, [isPlaying, saveProgress]);

  // Video event handlers
  const handleTimeUpdate = () => {
    if (videoRef.current) {
      const t = videoRef.current.currentTime;
      setCurrentTime(t);
      // Grow the furthest-watched marker only through natural playback
      if (t > maxWatchedRef.current) maxWatchedRef.current = t;
    }
  };

  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration);
      
      // Resume from last position
      const vp = progress[currentVideo?.id];
      const lastPos = vp?.lastPosition;
      // Initialize the furthest-watched marker from saved progress
      maxWatchedRef.current = Math.max(lastPos || 0, vp?.watchedSeconds || 0);
      if (lastPos && lastPos < videoRef.current.duration - 5) {
        videoRef.current.currentTime = lastPos;
      }
      setCurrentTime(videoRef.current.currentTime);
    }
  };

  // Quiz functions
  const fetchQuiz = async (videoId) => {
    try {
      setQuizLoading(true);
      const response = await axios.get(`${API}/courses/${courseId}/videos/${videoId}/quiz`, {
        headers: getAuthHeaders()
      });
      setQuiz(response.data);
      setQuizAnswers({});
      setQuizResult(null);
      
      // Check if already passed
      if (response.data.alreadyPassed) {
        setQuizPassed(prev => ({ ...prev, [videoId]: true }));
      }
      
      return response.data;
    } catch (error) {
      if (error.response?.status === 404) {
        return null; // No quiz for this video
      }
      console.error('Error fetching quiz:', error);
      return null;
    } finally {
      setQuizLoading(false);
    }
  };

  const handleQuizAnswer = (questionId, optionIndex, isMultiple = false) => {
    setQuizAnswers(prev => {
      if (isMultiple) {
        const current = prev[questionId] || [];
        if (current.includes(optionIndex)) {
          return { ...prev, [questionId]: current.filter(i => i !== optionIndex) };
        } else {
          return { ...prev, [questionId]: [...current, optionIndex] };
        }
      } else {
        return { ...prev, [questionId]: [optionIndex] };
      }
    });
  };

  const submitQuiz = async () => {
    if (!quiz) return;
    
    // Format answers for submission
    const answers = quiz.questions.map(q => ({
      questionId: q.id,
      selectedOptions: quizAnswers[q.id] || []
    }));
    
    setSubmittingQuiz(true);
    try {
      const response = await axios.post(
        `${API}/courses/${courseId}/videos/${currentVideo.id}/quiz/submit`,
        { answers },
        { headers: getAuthHeaders() }
      );
      
      setQuizResult(response.data);
      
      if (response.data.passed) {
        setQuizPassed(prev => ({ ...prev, [currentVideo.id]: true }));
        toast.success(t('courseWatch.quizPassed'));
      } else {
        toast.error(t('courseWatch.quizFailed'));
      }
    } catch (error) {
      console.error('Error submitting quiz:', error);
      toast.error(t('courseWatch.requestFailed'));
    } finally {
      setSubmittingQuiz(false);
    }
  };

  const retryQuiz = () => {
    setQuizResult(null);
    setQuizAnswers({});
  };

  const closeQuizAndAdvance = () => {
    setShowQuiz(false);
    setQuiz(null);
    setQuizResult(null);
    setQuizAnswers({});
    
    // Advance to next video
    const currentIndex = videos.findIndex(v => v.id === currentVideo?.id);
    if (currentIndex < videos.length - 1) {
      const nextVideo = videos[currentIndex + 1];
      toast.success(t('courseWatch.quizNext'));
      setTimeout(() => setCurrentVideo(nextVideo), 500);
    } else {
      toast.success(t('courseWatch.courseCompleted'));
    }
  };

  // Check if all curriculum is completed
  const isAllCompleted = videos.length > 0 && videos.every(video => {
    // Video must be completed
    const videoCompleted = progress[video.id]?.completed;
    // If video has quiz, quiz must be passed
    const quizOk = !video.quiz || quizPassed[video.id];
    return videoCompleted && quizOk;
  });

  // A video counts as "done" when watched to completion and (if it has a quiz) the quiz is passed
  const isVideoDone = (video) => {
    if (!video) return false;
    const completed = progress[video.id]?.completed;
    const quizOk = !video.quiz || quizPassed[video.id];
    return completed && quizOk;
  };

  // A video is unlocked only when every preceding video is done
  const isVideoUnlocked = (index) => {
    for (let i = 0; i < index; i++) {
      if (!isVideoDone(videos[i])) return false;
    }
    return true;
  };

  // Guarded video selection: block locked videos
  const selectVideo = (video, index) => {
    if (!isVideoUnlocked(index)) {
      toast.error(t('courseWatch.lockedVideoMsg'));
      return;
    }
    setCurrentVideo(video);
  };

  // Open the quiz for an unlocked video from the list - only allowed once the video is completed
  const openQuiz = (video, index) => {
    if (!isVideoUnlocked(index)) {
      toast.error(t('courseWatch.lockedVideoMsg'));
      return;
    }
    if (!progress[video.id]?.completed) {
      setCurrentVideo(video);
      toast.error(t('courseWatch.completeVideoFirst'));
      return;
    }
    setCurrentVideo(video);
    fetchQuiz(video.id).then((quizData) => {
      if (quizData) setShowQuiz(true);
    });
  };


  // Certificate request functions
  const requestCertificate = async (type) => {
    setCertRequestType(type);
    setShowCertDialog(true);
  };

  const submitCertificateRequest = async () => {
    if (selectedCertTypes.length === 0) {
      toast.error(t('courseWatch.selectAnswer'));
      return;
    }
    
    setSubmittingCertRequest(true);
    try {
      // Submit single request with multiple types
      await axios.post(`${API}/courses/${courseId}/certificate-request`, {
        types: selectedCertTypes,
        courseName: course?.name?.ja || course?.name,
        userName: user?.name
      }, {
        headers: getAuthHeaders()
      });
      
      const typeNames = selectedCertTypes.map(ct => 
        ct === 'card' 
          ? t('courseWatch.certTypeCard')
          : t('courseWatch.certTypeDigital')
      ).join('、');
      
      toast.success(t('courseWatch.requestSuccess'));
      setShowCertDialog(false);
      setSelectedCertTypes([]);
    } catch (error) {
      console.error('Certificate request error:', error);
      const errorDetail = error.response?.data?.detail;
      let errorMessage = t('courseWatch.requestFailed');
      
      if (errorDetail === 'Not all videos completed') {
        errorMessage = t('mp.completeVideos');
      } else if (errorDetail === 'Not all quizzes passed') {
        errorMessage = t('mp.passQuizzes');
      }
      
      toast.error(errorMessage);
    } finally {
      setSubmittingCertRequest(false);
    }
  };

  const handleVideoEnd = async () => {
    setIsPlaying(false);
    await saveProgress();
    
    // Check if this video has a quiz
    if (currentVideo?.quiz && !quizPassed[currentVideo.id]) {
      // Fetch and show quiz
      const quizData = await fetchQuiz(currentVideo.id);
      if (quizData && !quizData.alreadyPassed) {
        setShowQuiz(true);
        return;
      }
    }
    
    // Auto-advance to next video
    const currentIndex = videos.findIndex(v => v.id === currentVideo?.id);
    if (currentIndex < videos.length - 1) {
      const nextVideo = videos[currentIndex + 1];
      toast.success(t('courseWatch.quizNext'));
      setTimeout(() => setCurrentVideo(nextVideo), 1500);
    } else {
      toast.success(t('courseWatch.courseCompleted'));
    }
  };

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleSeek = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    let newTime = percent * duration;
    // Completed videos can fast-forward freely; others can't seek past the watched point
    const canFastForward = progress[currentVideo?.id]?.completed;
    if (!canFastForward && newTime > maxWatchedRef.current + 1) {
      newTime = maxWatchedRef.current;
      toast.error(t('courseWatch.noFastForward'));
    }
    if (videoRef.current) {
      videoRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    if (videoRef.current) {
      videoRef.current.volume = newVolume;
      setVolume(newVolume);
      setIsMuted(newVolume === 0);
    }
  };

  const toggleFullscreen = () => {
    const container = document.getElementById('video-container');
    if (!document.fullscreenElement) {
      container?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  const changePlaybackRate = () => {
    const rates = [0.5, 0.75, 1, 1.25, 1.5, 2];
    const currentIndex = rates.indexOf(playbackRate);
    const nextRate = rates[(currentIndex + 1) % rates.length];
    if (videoRef.current) {
      videoRef.current.playbackRate = nextRate;
      setPlaybackRate(nextRate);
    }
  };

  const skip = (seconds) => {
    if (videoRef.current) {
      let target = Math.max(0, Math.min(duration, currentTime + seconds));
      // Block skipping forward past the watched point unless the video is completed
      const canFastForward = progress[currentVideo?.id]?.completed;
      if (seconds > 0 && !canFastForward && target > maxWatchedRef.current + 1) {
        target = maxWatchedRef.current;
        toast.error(t('courseWatch.noFastForward'));
      }
      videoRef.current.currentTime = target;
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Hide controls after inactivity
  const handleMouseMove = () => {
    setShowControls(true);
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
    if (isPlaying) {
      controlsTimeoutRef.current = setTimeout(() => setShowControls(false), 3000);
    }
  };

  // Prevent right-click on video
  const handleContextMenu = (e) => {
    e.preventDefault();
    return false;
  };

  // Calculate overall progress
  const completedVideos = videos.filter(v => progress[v.id]?.completed).length;
  const overallProgress = videos.length > 0 ? (completedVideos / videos.length) * 100 : 0;

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-white" />
      </div>
    );
  }

  if (!course) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center text-white">
        <div className="text-center">
          <p className="text-xl mb-4">講習が見つかりません</p>
          <Button onClick={() => navigate('/courses')}>講習一覧に戻る</Button>
        </div>
      </div>
    );
  }

  if (videos.length === 0) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center text-white">
        <div className="text-center">
          <p className="text-xl mb-4">この講習には動画がまだありません</p>
          <Button onClick={() => navigate('/courses')}>講習一覧に戻る</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100" data-testid="course-watch">
      {/* Header - Same design as front page */}
      <nav className="sticky top-0 z-50 bg-white shadow-sm border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16 md:h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_jwsea-edu/artifacts/885p674v_image.png" 
                alt="JWSEA Logo" 
                className="h-8 md:h-12 w-auto"
              />
              <div>
                <span className="font-bold text-gray-900" style={{ fontSize: 'clamp(0.7rem, 2.5vw, 1rem)' }}>JWSEA</span>
                <span className="block text-gray-500" style={{ fontSize: 'clamp(0.55rem, 2vw, 0.75rem)' }}>Foundation</span>
              </div>
            </Link>

            {/* Course Title */}
            <div className="flex-1 mx-4 text-center hidden md:block">
              <h1 className="text-gray-900 font-semibold truncate">{course.name?.ja || course.name}</h1>
              <p className="text-gray-500 text-sm">{completedVideos}/{videos.length} {t('courseWatch.completed')}</p>
            </div>

            {/* Right Section */}
            <div className="flex items-center gap-2 sm:gap-4">
              {/* Progress */}
              <div className="hidden sm:flex items-center gap-3">
                <div className="w-32">
                  <Progress value={overallProgress} className="h-2" />
                </div>
                <span className="text-gray-600 text-sm font-medium">{Math.round(overallProgress)}%</span>
              </div>
              {/* Mobile progress */}
              <span className="sm:hidden text-xs text-gray-600 font-medium">{Math.round(overallProgress)}%</span>
              
              {/* Back Button */}
              <Button 
                variant="outline"
                size="sm" 
                onClick={() => navigate('/mypage')}
                className="border-gray-300 hover:border-[#00A651] hover:text-[#00A651] text-xs sm:text-sm px-2 sm:px-3"
              >
                <ArrowLeft className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">{t('navbar.mypage')}</span>
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Course Title */}
      <div className="md:hidden bg-white border-b border-gray-200 px-4 py-2">
        <h1 className="text-gray-900 font-semibold text-sm truncate">{course.name?.ja || course.name}</h1>
        <div className="flex items-center gap-2 mt-1">
          <Progress value={overallProgress} className="h-1.5 flex-1" />
          <span className="text-gray-500 text-xs">{Math.round(overallProgress)}%</span>
        </div>
      </div>

      {/* AI Face Monitoring - Required overlay */}
      {!cameraActive && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[100]" data-testid="face-monitor-required-overlay">
          <div className="bg-white rounded-2xl p-6 sm:p-8 max-w-lg mx-4 text-center shadow-2xl">
            <div className="bg-blue-100 rounded-full p-4 sm:p-5 w-20 h-20 sm:w-24 sm:h-24 mx-auto mb-4 sm:mb-5 flex items-center justify-center">
              <Camera className="h-10 w-10 sm:h-12 sm:w-12 text-blue-600" />
            </div>
            <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-2 sm:mb-3">AI受講監視の許可が必要です</h2>
            <p className="text-sm sm:text-base text-gray-600 mb-2">
              この講習では、受講者が動画を正しく視聴しているか確認するため、
              カメラを使用したAI監視が必要です。
            </p>
            <p className="text-xs sm:text-sm text-gray-500 mb-4 sm:mb-6">
              カメラ映像はリアルタイム顔検出のみに使用され、録画・保存はされません。
            </p>
            {cameraError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 text-red-700 text-sm flex items-center gap-2">
                <CameraOff className="h-4 w-4 flex-shrink-0" />
                <span>{cameraError}</span>
              </div>
            )}
            <button
              onClick={() => { initAlarm(); startCamera(); }}
              className="bg-[#00A651] hover:bg-[#008f45] text-white font-medium px-8 py-3 rounded-lg transition-colors flex items-center gap-2 mx-auto"
              data-testid="camera-permission-btn"
            >
              <Camera className="h-5 w-5" />
              カメラを許可して受講を開始
            </button>
          </div>
        </div>
      )}
      {cameraActive && (
        <div className="bg-green-50 border-b border-green-200 px-4 py-2 flex items-center justify-center gap-2" data-testid="face-monitor-active-banner">
          <Shield className="h-4 w-4 text-green-600" />
          <span className="text-green-700 text-sm">AI受講監視: {faceDetected ? '顔検出中 - 正常' : '顔未検出...'}</span>
          <span className={`inline-block w-2 h-2 rounded-full ${faceDetected ? 'bg-green-500' : 'bg-red-500 animate-pulse'}`} />
        </div>
      )}

      <div className="flex flex-col md:flex-row md:h-[calc(100vh-80px)]">
        {/* Video Player */}
        <div className="flex-1 flex flex-col">
          <div 
            id="video-container"
            className="relative bg-black aspect-video md:aspect-auto md:h-[80%] md:w-[80%] md:mx-auto md:my-auto flex items-center justify-center"
            onMouseMove={handleMouseMove}
            onContextMenu={handleContextMenu}
          >
            {videoLoading ? (
              <Loader2 className="h-12 w-12 animate-spin text-white" />
            ) : currentVideo && videoUrl ? (
              <>
                <video
                  ref={videoRef}
                  src={videoUrl}
                  className="w-full h-full object-contain"
                  crossOrigin="anonymous"
                  onTimeUpdate={handleTimeUpdate}
                  onLoadedMetadata={handleLoadedMetadata}
                  onEnded={handleVideoEnd}
                  onPlay={() => setIsPlaying(true)}
                  onPause={() => setIsPlaying(false)}
                  onWaiting={() => setVideoBuffering(true)}
                  onStalled={() => setVideoBuffering(true)}
                  onSeeking={() => setVideoBuffering(true)}
                  onPlaying={() => setVideoBuffering(false)}
                  onCanPlay={() => setVideoBuffering(false)}
                  onSeeked={() => setVideoBuffering(false)}
                  onClick={togglePlay}
                  onError={(e) => {
                    console.error('Video error:', e.target.error);
                    toast.error('動画の読み込みに失敗しました');
                  }}
                  controlsList="nodownload"
                  playsInline
                  preload={IS_MOBILE_DEVICE ? 'metadata' : 'auto'}
                />

                {/* Buffering spinner overlay */}
                {videoBuffering && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/30 z-30 pointer-events-none" data-testid="video-buffering-overlay">
                    <div className="flex flex-col items-center gap-2">
                      <Loader2 className="h-10 w-10 animate-spin text-white" />
                      <span className="text-white text-xs sm:text-sm">{t('courseWatch.buffering')}</span>
                    </div>
                  </div>
                )}
                
                {/* Face Monitor Camera Preview (PIP) */}
                {cameraActive && (
                  <div className="absolute top-2 right-2 w-16 h-12 sm:w-28 sm:h-20 rounded-lg overflow-hidden border-2 border-white/30 shadow-lg z-20" data-testid="face-monitor-pip">
                    <CameraPreview className="w-full h-full" />
                    <div className="absolute top-0.5 right-0.5 sm:top-1 sm:right-1">
                      <StatusBadge />
                    </div>
                  </div>
                )}

                {/* Face Monitor Warning Overlay */}
                <WarningOverlay onResume={() => {
                  if (videoRef.current) {
                    videoRef.current.play();
                    setIsPlaying(true);
                  }
                }} />
                
                {/* Custom Controls */}
                <div 
                  className={`absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2 sm:p-4 transition-opacity ${showControls ? 'opacity-100' : 'opacity-0'}`}
                >
                  {/* Progress Bar */}
                  <div 
                    className="w-full h-2 sm:h-1 bg-gray-600 rounded cursor-pointer mb-2 sm:mb-3"
                    onClick={handleSeek}
                  >
                    <div 
                      className="h-full bg-[#00A651] rounded relative"
                      style={{ width: `${(currentTime / duration) * 100}%` }}
                    >
                      <div className="absolute right-0 top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full shadow" />
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1 sm:gap-3">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={togglePlay}
                        className="text-white hover:bg-white/20 p-1 sm:p-2"
                      >
                        {isPlaying ? <Pause className="h-4 w-4 sm:h-5 sm:w-5" /> : <Play className="h-4 w-4 sm:h-5 sm:w-5" />}
                      </Button>
                      
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => skip(-10)}
                        className="text-white hover:bg-white/20 p-1 sm:p-2 hidden sm:flex"
                      >
                        <SkipBack className="h-4 w-4" />
                      </Button>
                      
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => skip(10)}
                        className="text-white hover:bg-white/20 p-1 sm:p-2 hidden sm:flex"
                      >
                        <SkipForward className="h-4 w-4" />
                      </Button>
                      
                      <div className="hidden sm:flex items-center gap-2">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={toggleMute}
                          className="text-white hover:bg-white/20"
                        >
                          {isMuted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
                        </Button>
                        <input
                          type="range"
                          min="0"
                          max="1"
                          step="0.1"
                          value={isMuted ? 0 : volume}
                          onChange={handleVolumeChange}
                          className="w-20 h-1 accent-[#00A651]"
                        />
                      </div>
                      
                      <span className="text-white text-[10px] sm:text-sm ml-1">
                        {formatTime(currentTime)} / {formatTime(duration)}
                      </span>
                    </div>
                    
                    <div className="flex items-center gap-1 sm:gap-3">
                      {/* Face Monitor Status Indicator */}
                      {cameraActive && (
                        <div className={`hidden sm:flex items-center gap-1 text-xs ${faceDetected ? 'text-green-400' : 'text-red-400 animate-pulse'}`}>
                          <Camera className="h-3.5 w-3.5" />
                          <span>{faceDetected ? 'AI監視中' : '未検出'}</span>
                        </div>
                      )}

                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={changePlaybackRate}
                        className="text-white hover:bg-white/20 text-xs sm:text-sm p-1 sm:p-2"
                      >
                        {playbackRate}x
                      </Button>
                      
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={toggleFullscreen}
                        className="text-white hover:bg-white/20 p-1 sm:p-2"
                      >
                        {isFullscreen ? <Minimize className="h-4 w-4" /> : <Maximize className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <p className="text-gray-400">動画を選択してください</p>
            )}
          </div>
          
          {/* Current Video Info */}
          {currentVideo && (
            <div className="bg-white border-t border-gray-200 p-3 sm:p-4">
              <h2 className="text-gray-900 font-semibold text-base sm:text-lg">{locQuiz(currentVideo.title)}</h2>
              {locQuiz(currentVideo.description) && (
                <p className="text-gray-600 text-xs sm:text-sm mt-1">{locQuiz(currentVideo.description)}</p>
              )}
            </div>
          )}

          {/* Mobile: Video List + Certificate below video */}
          <div ref={mobileListRef} className="md:hidden overflow-y-auto bg-gray-50 max-h-[55vh]">
            {/* Video List */}
            <div className="bg-white border-t border-gray-200">
              <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
                <h3 className="text-gray-900 font-semibold text-sm flex items-center gap-2">
                  <BookOpen className="h-4 w-4 text-[#00A651]" />
                  {t('courseWatch.videoList')} ({videos.length})
                </h3>
              </div>
              <div className="divide-y divide-gray-50">
                {videos.map((video, index) => {
                  const videoProgress = progress[video.id];
                  const isCompleted = videoProgress?.completed;
                  const isCurrent = currentVideo?.id === video.id;
                  const watchedPercent = videoProgress?.watchedPercent || 0;
                  const hasQuiz = video.quiz;
                  const quizPassedStatus = quizPassed[video.id];
                  const isLocked = !isVideoUnlocked(index);
                  return (
                    <div
                      key={video.id}
                      ref={isCurrent ? mobileCurrentRef : null}
                      className={`px-4 py-3 flex items-center gap-3 ${isLocked ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer active:bg-gray-50'} ${isCurrent ? 'bg-[#e6f6ec] border-l-4 border-[#00A651]' : ''}`}
                      onClick={() => selectVideo(video, index)}
                      data-testid={`mobile-video-item-${index}`}
                    >
                      <div className="flex-shrink-0">
                        {isCompleted
                          ? <CheckCircle className="h-5 w-5 text-[#00A651]" />
                          : isCurrent
                            ? <Play className="h-5 w-5 text-[#00A651]" />
                            : isLocked
                              ? <Lock className="h-5 w-5 text-gray-300" />
                              : <Circle className="h-5 w-5 text-gray-300" />
                        }
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm truncate ${isCurrent ? 'text-[#00A651] font-medium' : 'text-gray-700'}`}>
                          {index + 1}. {locQuiz(video.title)}
                        </p>
                        {watchedPercent > 0 && !isCompleted && (
                          <Progress value={watchedPercent} className="h-1 mt-1" />
                        )}
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {isCurrent && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#00A651] text-white font-medium" data-testid={`mobile-watching-badge-${index}`}>
                            {t('courseWatch.watching')}
                          </span>
                        )}
                        {hasQuiz && (
                          <button
                            type="button"
                            disabled={isLocked}
                            onClick={(e) => { e.stopPropagation(); openQuiz(video, index); }}
                            className={`text-[10px] px-1.5 py-0.5 rounded-full ${isLocked ? 'cursor-not-allowed' : 'cursor-pointer active:opacity-70'} ${quizPassedStatus ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}`}
                            data-testid={`mobile-quiz-btn-${index}`}
                          >
                            {quizPassedStatus ? t('courseWatch.passed') : t('courseWatch.quiz')}
                          </button>
                        )}
                        {isCompleted && <span className="text-[10px] text-[#00A651]">{t('courseWatch.completed')}</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Certificate Request Section - Mobile */}
            {isAllCompleted && (
              <div className="m-3 p-4 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg">
                <div className="flex items-center gap-2 mb-3">
                  <Award className="h-5 w-5 text-[#00A651]" />
                  <h3 className="font-semibold text-[#00A651] text-sm">
                    {t('courseWatch.courseCompleted')}
                  </h3>
                </div>
                <p className="text-xs text-gray-600 mb-3">
                  {t('courseWatch.selectCertType')}
                </p>
                <div className="mb-3 space-y-2">
                  <label className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer ${selectedCertTypes.includes('card') ? 'border-[#00A651] bg-green-50' : 'border-gray-200'}`}>
                    <input type="checkbox" value="card" checked={selectedCertTypes.includes('card')}
                      onChange={(e) => e.target.checked ? setSelectedCertTypes([...selectedCertTypes, 'card']) : setSelectedCertTypes(selectedCertTypes.filter(ct => ct !== 'card'))}
                      className="h-4 w-4 text-[#00A651] rounded" />
                    <CreditCard className={`h-4 w-4 ${selectedCertTypes.includes('card') ? 'text-[#00A651]' : 'text-gray-400'}`} />
                    <div>
                      <p className={`text-sm font-medium ${selectedCertTypes.includes('card') ? 'text-[#00A651]' : 'text-gray-700'}`}>{t('courseWatch.certTypeCard')}</p>
                      <p className="text-[10px] text-gray-500">{t('courseWatch.certTypeCardDesc')}</p>
                    </div>
                  </label>
                  <label className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer ${selectedCertTypes.includes('digital') ? 'border-[#00A651] bg-green-50' : 'border-gray-200'}`}>
                    <input type="checkbox" value="digital" checked={selectedCertTypes.includes('digital')}
                      onChange={(e) => e.target.checked ? setSelectedCertTypes([...selectedCertTypes, 'digital']) : setSelectedCertTypes(selectedCertTypes.filter(ct => ct !== 'digital'))}
                      className="h-4 w-4 text-[#00A651] rounded" />
                    <FileText className={`h-4 w-4 ${selectedCertTypes.includes('digital') ? 'text-[#00A651]' : 'text-gray-400'}`} />
                    <div>
                      <p className={`text-sm font-medium ${selectedCertTypes.includes('digital') ? 'text-[#00A651]' : 'text-gray-700'}`}>{t('courseWatch.certTypeDigital')}</p>
                      <p className="text-[10px] text-gray-500">{t('courseWatch.certTypeDigitalDesc')}</p>
                    </div>
                  </label>
                </div>
                <Button onClick={() => submitCertificateRequest()} disabled={selectedCertTypes.length === 0 || submittingCertRequest}
                  className={`w-full ${selectedCertTypes.length > 0 ? 'bg-[#00A651] hover:bg-[#008a43]' : 'bg-gray-300 cursor-not-allowed'}`}>
                  {submittingCertRequest ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{t('courseWatch.submitting')}</> : <><Send className="h-4 w-4 mr-2" />{t('courseWatch.submitRequest')}</>}
                </Button>
              </div>
            )}
            {/* Bottom padding for mobile */}
            <div className="h-4" />
          </div>
        </div>

        {/* Video List Sidebar - Desktop */}
        <div ref={desktopListRef} className="w-96 lg:w-[460px] xl:w-[552px] bg-white border-l border-gray-200 overflow-y-auto hidden md:block">
          <div className="p-3 border-b border-gray-200 bg-gray-50">
            <h3 className="text-gray-900 font-semibold flex items-center gap-2">
              <BookOpen className="h-4 w-4 text-[#00A651]" />
              {t('courseWatch.videoList')}
            </h3>
            <p className="text-gray-500 text-sm mt-1">{videos.length} videos</p>
          </div>
          
          <div className="p-1">
            {videos.map((video, index) => {
              const videoProgress = progress[video.id];
              const isCompleted = videoProgress?.completed;
              const isCurrent = currentVideo?.id === video.id;
              const watchedPercent = videoProgress?.watchedPercent || 0;
              const hasQuiz = video.quiz;
              const quizPassedStatus = quizPassed[video.id];
              const isLocked = !isVideoUnlocked(index);
              
              return (
                <div
                  key={video.id}
                  ref={isCurrent ? desktopCurrentRef : null}
                  className={`p-2 rounded-lg transition-colors mb-1 ${
                    isCurrent 
                      ? 'bg-[#e6f6ec] border-2 border-[#00A651] shadow-sm' 
                      : isLocked
                        ? 'opacity-50 cursor-not-allowed border border-transparent'
                        : 'cursor-pointer hover:bg-gray-100 border border-transparent'
                  }`}
                  onClick={() => selectVideo(video, index)}
                  data-testid={`desktop-video-item-${index}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 mt-1">
                      {isCompleted ? (
                        <CheckCircle className="h-5 w-5 text-[#00A651]" />
                      ) : isCurrent ? (
                        <Play className="h-5 w-5 text-[#00A651]" />
                      ) : isLocked ? (
                        <Lock className="h-5 w-5 text-gray-400" />
                      ) : (
                        <Circle className="h-5 w-5 text-gray-400" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-gray-400 text-xs">{index + 1}.</span>
                        <p className={`text-sm font-medium break-words ${isCurrent ? 'text-[#00A651]' : 'text-gray-700'}`}>
                          {locQuiz(video.title)}
                        </p>
                        {isCurrent && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#00A651] text-white font-medium flex-shrink-0" data-testid={`desktop-watching-badge-${index}`}>
                            {t('courseWatch.watching')}
                          </span>
                        )}
                      </div>
                      {watchedPercent > 0 && !isCompleted && (
                        <div className="mt-2">
                          <Progress value={watchedPercent} className="h-1" />
                          <p className="text-xs text-gray-500 mt-1">{Math.round(watchedPercent)}% {t('courseWatch.completed')}</p>
                        </div>
                      )}
                      {/* Quiz indicator */}
                      {hasQuiz && (
                        <div 
                          className={`mt-2 flex items-center gap-1 ${isLocked ? '' : 'cursor-pointer hover:opacity-80'}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            openQuiz(video, index);
                          }}
                          data-testid={`desktop-quiz-btn-${index}`}
                        >
                          <FileQuestion className="h-3 w-3 text-orange-500" />
                          <span className={`text-xs ${quizPassedStatus ? 'text-[#00A651] hover:underline' : 'text-orange-500 hover:underline'}`}>
                            {quizPassedStatus 
                              ? t('courseWatch.passed')
                              : t('courseWatch.quiz')}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Mobile Video List Toggle removed - now inline below video */}

        {/* Certificate Request Section - Desktop (Show when all curriculum completed) */}
        {isAllCompleted && (
          <div className="hidden md:block mt-4 p-4 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg">
            <div className="flex items-center gap-2 mb-3">
              <Award className="h-5 w-5 text-[#00A651]" />
              <h3 className="font-semibold text-[#00A651]">
                {t('courseWatch.courseCompleted')}
              </h3>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              {t('courseWatch.selectCertType')}
            </p>
            
            {/* Certificate type selection */}
            <div className="mb-4">
              <p className="text-sm font-medium text-gray-700 mb-2">
                {t('courseWatch.selectCertType')}
              </p>
              <div className="flex flex-col gap-2">
                <label 
                  className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-all ${
                    selectedCertTypes.includes('card') 
                      ? 'border-[#00A651] bg-green-50' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <input 
                    type="checkbox" 
                    value="card"
                    checked={selectedCertTypes.includes('card')}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedCertTypes([...selectedCertTypes, 'card']);
                      } else {
                        setSelectedCertTypes(selectedCertTypes.filter(t => t !== 'card'));
                      }
                    }}
                    className="h-4 w-4 text-[#00A651] focus:ring-[#00A651] rounded"
                  />
                  <CreditCard className={`h-5 w-5 ${selectedCertTypes.includes('card') ? 'text-[#00A651]' : 'text-gray-400'}`} />
                  <div>
                    <p className={`font-medium ${selectedCertTypes.includes('card') ? 'text-[#00A651]' : 'text-gray-700'}`}>
                      {t('courseWatch.certTypeCard')}
                    </p>
                    <p className="text-xs text-gray-500">
                      {t('courseWatch.certTypeCardDesc')}
                    </p>
                  </div>
                </label>
                <label 
                  className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-all ${
                    selectedCertTypes.includes('digital') 
                      ? 'border-[#00A651] bg-green-50' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <input 
                    type="checkbox" 
                    value="digital"
                    checked={selectedCertTypes.includes('digital')}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedCertTypes([...selectedCertTypes, 'digital']);
                      } else {
                        setSelectedCertTypes(selectedCertTypes.filter(t => t !== 'digital'));
                      }
                    }}
                    className="h-4 w-4 text-[#00A651] focus:ring-[#00A651] rounded"
                  />
                  <FileText className={`h-5 w-5 ${selectedCertTypes.includes('digital') ? 'text-[#00A651]' : 'text-gray-400'}`} />
                  <div>
                    <p className={`font-medium ${selectedCertTypes.includes('digital') ? 'text-[#00A651]' : 'text-gray-700'}`}>
                      {t('courseWatch.certTypeDigital')}
                    </p>
                    <p className="text-xs text-gray-500">
                      {t('courseWatch.certTypeDigitalDesc')}
                    </p>
                  </div>
                </label>
              </div>
            </div>
            
            {/* Submit button - enabled only when at least one type is selected */}
            <Button 
              onClick={() => submitCertificateRequest()}
              disabled={selectedCertTypes.length === 0 || submittingCertRequest}
              className={`w-full ${
                selectedCertTypes.length > 0 
                  ? 'bg-[#00A651] hover:bg-[#008a43]' 
                  : 'bg-gray-300 cursor-not-allowed'
              }`}
            >
              {submittingCertRequest ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t('courseWatch.submitting')}
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  {t('courseWatch.submitRequest')}
                </>
              )}
            </Button>
          </div>
        )}
      </div>

      {/* Quiz Dialog */}
      <Dialog open={showQuiz} onOpenChange={(open) => {
        setShowQuiz(open);
      }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileQuestion className="h-5 w-5 text-orange-500" />
              {quiz?.title || t('courseWatch.quizTitle')}
            </DialogTitle>
            <DialogDescription>
              {t('courseWatch.quizPassScore')}: {quiz?.passingScore || 80}%
              {quiz?.timeLimit && ` | ${t('courseWatch.minutes')}: ${quiz.timeLimit}`}
            </DialogDescription>
          </DialogHeader>

          {quizLoading ? (
            <div className="py-8 text-center">
              <Loader2 className="h-8 w-8 animate-spin mx-auto text-orange-500" />
            </div>
          ) : quizResult ? (
            // Show quiz results
            <div className="py-4">
              <div className={`text-center p-6 rounded-lg mb-4 ${quizResult.passed ? 'bg-green-50' : 'bg-red-50'}`}>
                {quizResult.passed ? (
                  <Award className="h-16 w-16 text-[#00A651] mx-auto mb-3" />
                ) : (
                  <XCircle className="h-16 w-16 text-red-500 mx-auto mb-3" />
                )}
                <h3 className={`text-2xl font-bold ${quizResult.passed ? 'text-[#00A651]' : 'text-red-600'}`}>
                  {quizResult.passed 
                    ? t('courseWatch.quizPassed')
                    : t('courseWatch.quizFailed')}
                </h3>
                <p className="text-3xl font-bold mt-2">{quizResult.scorePercent}%</p>
                <p className="text-gray-600 mt-1">
                  {quizResult.earnedPoints} / {quizResult.totalPoints} {t('courseWatch.quizScore')}
                </p>
              </div>

              {/* Show correct/incorrect for each question */}
              <div className="space-y-3">
                {quiz?.questions.map((q, i) => {
                  const result = quizResult.results?.find(r => r.questionId === q.id);
                  return (
                    <div key={q.id} className={`p-3 rounded border ${result?.isCorrect ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}`}>
                      <div className="flex items-start gap-2">
                        {result?.isCorrect ? (
                          <CheckCircle className="h-5 w-5 text-[#00A651] flex-shrink-0 mt-0.5" />
                        ) : (
                          <XCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                        )}
                        <div>
                          <p className="font-medium text-sm">{t('courseWatch.question')}{i + 1}: {locQuiz(q.questionText)}</p>
                          {!result?.isCorrect && (
                            <p className="text-xs text-red-600 mt-1">
                              {t('mp.correctAnswer')}
                              {result?.correctOptions?.map(idx => locQuiz(q.options[idx]?.text)).join(', ')}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <DialogFooter className="mt-6">
                {quizResult.passed ? (
                  <Button onClick={closeQuizAndAdvance} className="bg-[#00A651] hover:bg-[#008a43]">
                    {t('courseWatch.quizNext')}
                  </Button>
                ) : (
                  <Button onClick={retryQuiz} className="bg-orange-500 hover:bg-orange-600">
                    <RotateCcw className="h-4 w-4 mr-2" />
                    {t('courseWatch.quizRetry')}
                  </Button>
                )}
              </DialogFooter>
            </div>
          ) : (
            // Show quiz questions
            <div className="py-4 space-y-6">
              {quiz?.questions.map((question, qIndex) => (
                <div key={question.id} className="border rounded-lg p-4">
                  <p className="font-medium mb-3">
                    <span className="text-orange-500 mr-2">{t('courseWatch.question')}{qIndex + 1}.</span>
                    {locQuiz(question.questionText)}
                  </p>
                  
                  {question.questionType === 'multiple' ? (
                    // Multiple choice (checkboxes)
                    <div className="space-y-2">
                      {question.options.map((option, oIndex) => (
                        <div key={oIndex} className="flex items-center space-x-2">
                          <Checkbox
                            id={`q${qIndex}-o${oIndex}`}
                            checked={(quizAnswers[question.id] || []).includes(oIndex)}
                            onCheckedChange={() => handleQuizAnswer(question.id, oIndex, true)}
                          />
                          <Label htmlFor={`q${qIndex}-o${oIndex}`} className="cursor-pointer">
                            {locQuiz(option.text)}
                          </Label>
                        </div>
                      ))}
                    </div>
                  ) : (
                    // Single choice (radio)
                    <RadioGroup
                      value={String(quizAnswers[question.id]?.[0] ?? '')}
                      onValueChange={(value) => handleQuizAnswer(question.id, parseInt(value))}
                    >
                      {question.options.map((option, oIndex) => (
                        <div key={oIndex} className="flex items-center space-x-2">
                          <RadioGroupItem value={String(oIndex)} id={`q${qIndex}-o${oIndex}`} />
                          <Label htmlFor={`q${qIndex}-o${oIndex}`} className="cursor-pointer">
                            {locQuiz(option.text)}
                          </Label>
                        </div>
                      ))}
                    </RadioGroup>
                  )}
                </div>
              ))}

              <DialogFooter>
                <Button 
                  onClick={submitQuiz} 
                  disabled={submittingQuiz || Object.keys(quizAnswers).length < (quiz?.questions?.length || 0)}
                  className="bg-orange-500 hover:bg-orange-600"
                >
                  {submittingQuiz ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> {t('courseWatch.submittingQuiz')}</>
                  ) : (
                    t('courseWatch.submitQuiz')
                  )}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Certificate Request Dialog */}
      <Dialog open={showCertDialog} onOpenChange={setShowCertDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {certRequestType === 'card' ? (
                <CreditCard className="h-5 w-5 text-[#00A651]" />
              ) : (
                <FileText className="h-5 w-5 text-[#00A651]" />
              )}
              {certRequestType === 'card'
                ? t('courseWatch.certTypeCard')
                : t('courseWatch.certTypeDigital')}
            </DialogTitle>
            <DialogDescription>
              {certRequestType === 'card'
                ? t('courseWatch.certTypeCardDesc')
                : t('courseWatch.certTypeDigitalDesc')}
            </DialogDescription>
          </DialogHeader>

          <div className="py-4 space-y-4">
            <div className="bg-gray-50 p-4 rounded-lg space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">{t('courseWatch.quizTitle')}</span>
                <span className="font-medium">{course?.name?.ja || course?.name}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">{t('register.name')}</span>
                <span className="font-medium">{user?.name}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">{t('courseWatch.certificateRequest')}</span>
                <span className="font-medium">
                  {certRequestType === 'card' 
                    ? t('courseWatch.certTypeCard')
                    : t('courseWatch.certTypeDigital')}
                </span>
              </div>
            </div>

            {certRequestType === 'card' && (
              <div className="text-sm text-amber-600 bg-amber-50 p-3 rounded-lg">
                <p className="font-medium">{t('mp.note')}</p>
                <p>{t('mp.cardCertNote')}</p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCertDialog(false)}>
              {t('courseWatch.cancel')}
            </Button>
            <Button 
              onClick={submitCertificateRequest}
              disabled={submittingCertRequest}
              className="bg-[#00A651] hover:bg-[#008a43]"
            >
              {submittingCertRequest ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> {t('courseWatch.submitting')}</>
              ) : (
                <><Send className="h-4 w-4 mr-2" /> {t('courseWatch.submitRequest')}</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
